import React, { useState, useEffect } from 'react';
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import {
  Loader2, Video, Heart, Sparkles, Clock, Calendar, Save, Trash2,
  Play, Link2, FileText, Send, Plus, X, BookOpen, Users,
  MessageCircle, ChevronDown, ChevronUp, Phone, Mail, ArrowRight,
  VideoOff
} from 'lucide-react';
import VideoRoom from "../components/VideoRoom";

const API = process.env.REACT_APP_BACKEND_URL;

// ═══════════════════════════════════════════════════════
// NL OPERATIONS EXPANSION
// ═══════════════════════════════════════════════════════
function NLExpansion() {
  const [savedQueries, setSavedQueries] = useState([]);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [newName, setNewName] = useState('');
  const [newQuery, setNewQuery] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([
      fetch(`${API}/api/wave4/nl/saved-queries`).then(r => r.json()),
      fetch(`${API}/api/wave4/nl/query-history?limit=20`).then(r => r.json()),
    ]).then(([sq, h]) => {
      setSavedQueries(sq.queries || []);
      setHistory(h.history || []);
    }).catch(() => {});
  }, []);

  const saveQuery = async () => {
    if (!newName.trim() || !newQuery.trim()) return;
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/wave4/nl/save-query`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newName, query: newQuery }),
      });
      const data = await res.json();
      setSavedQueries(prev => [data, ...prev]);
      setNewName(''); setNewQuery('');
    } catch {}
    setSaving(false);
  };

  const deleteQuery = async (id) => {
    try {
      await fetch(`${API}/api/wave4/nl/saved-query/${id}`, { method: 'DELETE' });
      setSavedQueries(prev => prev.filter(q => q.id !== id));
    } catch {}
  };

  const exportReport = async () => {
    const ids = history.filter(h => h.id).map(h => h.id).slice(0, 10);
    if (ids.length === 0) return;
    try {
      const res = await fetch(`${API}/api/wave4/nl/export-report`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(ids),
      });
      const data = await res.json();
      const blob = new Blob([data.content], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = 'operations_report.txt'; a.click();
      URL.revokeObjectURL(url);
    } catch {}
  };

  return (
    <div data-testid="nl-expansion">
      <div className="flex items-center gap-2 mb-4">
        <Sparkles className="w-5 h-5 text-sky-400" />
        <h2 className="text-lg font-heading font-bold text-[#2C3D30]">NL Operations</h2>
        <Badge className="bg-sky-50 text-sky-500 text-xs">Expanded</Badge>
      </div>

      {/* Save new query */}
      <div className="p-3 rounded-xl bg-sky-50 border border-sky-100 mb-4" data-testid="nl-save-form">
        <p className="text-xs font-medium text-sky-700 mb-2">Save a Frequent Query</p>
        <div className="space-y-2">
          <Input value={newName} onChange={e => setNewName(e.target.value)} placeholder="Query name (e.g., 'Morning Census')"
            className="text-sm" data-testid="nl-save-name" />
          <Input value={newQuery} onChange={e => setNewQuery(e.target.value)} placeholder="Query text (e.g., 'Show me all high-pain patients')"
            className="text-sm" data-testid="nl-save-query" />
          <Button onClick={saveQuery} disabled={saving || !newName.trim()} size="sm" className="bg-sky-300 hover:bg-sky-400"
            data-testid="nl-save-btn">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Save className="w-3.5 h-3.5 mr-1" />}
            Save Query
          </Button>
        </div>
      </div>

      {/* Saved queries */}
      {savedQueries.length > 0 && (
        <div className="mb-4" data-testid="nl-saved-list">
          <p className="text-xs font-medium text-[#5C6A5F] mb-2">Saved Queries</p>
          <div className="space-y-2">
            {savedQueries.map(q => (
              <div key={q.id} className="flex items-center justify-between p-2.5 rounded-xl bg-sand-50 border border-sand-200">
                <div>
                  <p className="text-sm font-medium text-[#2C3D30]">{q.name}</p>
                  <p className="text-xs text-sand-400">{q.query}</p>
                </div>
                <button onClick={() => deleteQuery(q.id)} className="p-1 hover:bg-terra-50 rounded text-sand-400 hover:text-terra-400">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* History + Export */}
      <div className="flex items-center justify-between mb-2">
        <button onClick={() => setShowHistory(!showHistory)} className="text-xs text-[#5C6A5F] flex items-center gap-1 hover:text-[#2C3D30]"
          data-testid="nl-history-toggle">
          {showHistory ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          Query History ({history.length})
        </button>
        {history.length > 0 && (
          <Button variant="outline" size="sm" onClick={exportReport} className="text-xs" data-testid="nl-export-btn">
            <FileText className="w-3 h-3 mr-1" /> Export Report
          </Button>
        )}
      </div>

      {showHistory && history.map((h, i) => (
        <div key={i} className="mb-2 p-2.5 rounded-xl bg-sand-100 border border-sand-200">
          <p className="text-xs font-medium text-[#5C6A5F]">Q: {h.query}</p>
          <p className="text-xs text-[#5C6A5F] mt-1 line-clamp-2">{h.answer}</p>
          <p className="text-xs text-sand-400 mt-1">{new Date(h.timestamp).toLocaleString()}</p>
        </div>
      ))}
    </div>
  );
}


// ═══════════════════════════════════════════════════════
// VIDEO VISITS
// ═══════════════════════════════════════════════════════
function VideoVisits({ patients }) {
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState('');
  const [scheduledTime, setScheduledTime] = useState('');
  const [duration, setDuration] = useState(30);
  const [visitType, setVisitType] = useState('family');
  const [scheduling, setScheduling] = useState(false);
  const [notesVisit, setNotesVisit] = useState(null);
  const [notesText, setNotesText] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);
  const [activeCall, setActiveCall] = useState(null);

  useEffect(() => {
    fetch(`${API}/api/wave4/video-visit/list?status=all`)
      .then(r => r.json()).then(d => setVisits(d.visits || []))
      .catch(() => {}).finally(() => setLoading(false));
  }, []);

  const schedule = async () => {
    if (!selectedPatient || !scheduledTime) return;
    setScheduling(true);
    try {
      const res = await fetch(`${API}/api/wave4/video-visit/schedule`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ patient_id: selectedPatient, scheduled_time: scheduledTime, duration_minutes: duration, visit_type: visitType }),
      });
      const visit = await res.json();
      setVisits(prev => [visit, ...prev]);
      setShowForm(false); setSelectedPatient(''); setScheduledTime('');
    } catch {}
    setScheduling(false);
  };

  const saveNotes = async () => {
    if (!notesVisit || !notesText.trim()) return;
    setSavingNotes(true);
    try {
      const res = await fetch(`${API}/api/wave4/video-visit/${notesVisit.id}/notes`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ visit_id: notesVisit.id, notes: notesText }),
      });
      const data = await res.json();
      setVisits(prev => prev.map(v => v.id === notesVisit.id ? { ...v, ai_summary: data.ai_summary, status: 'completed' } : v));
      setNotesVisit(null); setNotesText('');
    } catch {}
    setSavingNotes(false);
  };

  const statusColors = {
    scheduled: 'bg-sky-50 text-sky-500',
    'in-progress': 'bg-sage-50 text-sage-400',
    completed: 'bg-sand-200 text-[#5C6A5F]',
    cancelled: 'bg-red-100 text-terra-400',
  };

  return (
    <div data-testid="video-visits">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Video className="w-5 h-5 text-sage-400" />
          <h2 className="text-lg font-heading font-bold text-[#2C3D30]">Video Visits</h2>
          <Badge className="bg-sage-50 text-sage-400 text-xs">Telehealth</Badge>
        </div>
        <Button onClick={() => setShowForm(!showForm)} size="sm" className="bg-sage-400 hover:bg-sage-500"
          data-testid="vv-new-btn">
          <Plus className="w-3.5 h-3.5 mr-1" /> Schedule Visit
        </Button>
      </div>

      {showForm && (
        <Card className="mb-4 border-sage-200" data-testid="vv-schedule-form">
          <CardContent className="pt-4 space-y-3">
            <select value={selectedPatient} onChange={e => setSelectedPatient(e.target.value)}
              className="w-full rounded-xl border border-sand-200 px-3 py-2 text-sm bg-sand-50" data-testid="vv-patient-select">
              <option value="">Select patient...</option>
              {patients.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
            <Input type="datetime-local" value={scheduledTime} onChange={e => setScheduledTime(e.target.value)}
              data-testid="vv-time-input" />
            <div className="flex gap-2">
              <select value={duration} onChange={e => setDuration(Number(e.target.value))}
                className="flex-1 rounded-xl border border-sand-200 px-3 py-2 text-sm bg-sand-50" data-testid="vv-duration">
                <option value={15}>15 min</option>
                <option value={30}>30 min</option>
                <option value={45}>45 min</option>
                <option value={60}>60 min</option>
              </select>
              <select value={visitType} onChange={e => setVisitType(e.target.value)}
                className="flex-1 rounded-xl border border-sand-200 px-3 py-2 text-sm bg-sand-50" data-testid="vv-type">
                <option value="family">Family Visit</option>
                <option value="clinical">Clinical Check-in</option>
                <option value="idg">IDG Meeting</option>
              </select>
            </div>
            <div className="flex gap-2">
              <Button onClick={schedule} disabled={scheduling || !selectedPatient || !scheduledTime}
                className="flex-1 bg-sage-400 hover:bg-sage-500" data-testid="vv-schedule-btn">
                {scheduling ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Schedule'}
              </Button>
              <Button variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Active Video Call */}
      {activeCall && (
        <div className="mb-4" data-testid="vv-active-call">
          <VideoRoom
            visitCode={activeCall.visit_code}
            patientName={activeCall.patient_name}
            role="staff"
            onEnd={() => {
              setActiveCall(null);
              setVisits(prev => prev.map(v => v.id === activeCall.id ? { ...v, status: 'completed' } : v));
            }}
          />
        </div>
      )}

      {/* Notes Modal */}
      {notesVisit && (
        <Card className="mb-4 border-terra-200" data-testid="vv-notes-form">
          <CardContent className="pt-4 space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium text-[#2C3D30]">Visit Notes — {notesVisit.patient_name}</span>
              <button onClick={() => setNotesVisit(null)} className="text-sand-400 hover:text-[#5C6A5F]"><X className="w-4 h-4" /></button>
            </div>
            <textarea value={notesText} onChange={e => setNotesText(e.target.value)}
              placeholder="Enter visit observations, patient status, recommendations..."
              className="w-full rounded-xl border border-sand-200 px-3 py-2.5 text-sm min-h-[100px] resize-none"
              data-testid="vv-notes-input" />
            <Button onClick={saveNotes} disabled={savingNotes || !notesText.trim()} className="w-full bg-terra-400 hover:bg-terra-500"
              data-testid="vv-notes-save-btn">
              {savingNotes ? <><Loader2 className="w-4 h-4 animate-spin mr-1" />Generating AI summary...</> : <><Save className="w-4 h-4 mr-1" />Save & Generate Summary</>}
            </Button>
          </CardContent>
        </Card>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-sage-400" /></div>
      ) : visits.length === 0 ? (
        <div className="text-center py-12 text-sand-400">
          <Video className="w-8 h-8 mx-auto mb-2 opacity-50" />
          <p className="text-sm">No video visits scheduled yet.</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="vv-list">
          {visits.map(v => (
            <Card key={v.id} className="border-l-4 border-l-sage-400" data-testid={`vv-visit-${v.id}`}>
              <CardContent className="pt-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-semibold text-[#2C3D30]">{v.patient_name}</span>
                  <Badge className={statusColors[v.status] || 'bg-sand-200'}>{v.status}</Badge>
                </div>
                <div className="flex items-center gap-3 text-xs text-[#5C6A5F] mb-2">
                  <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{new Date(v.scheduled_time).toLocaleString()}</span>
                  <span>{v.duration_minutes} min</span>
                  <Badge variant="outline" className="text-xs">{v.visit_type}</Badge>
                </div>
                {v.ai_summary && (
                  <div className="mt-2 p-2.5 rounded-xl bg-terra-50 border border-terra-100">
                    <p className="text-xs font-medium text-terra-700 mb-1">AI Visit Summary</p>
                    <p className="text-xs text-[#5C6A5F] whitespace-pre-wrap">{v.ai_summary}</p>
                  </div>
                )}
                <div className="flex gap-2 mt-2">
                  {v.status === 'scheduled' && (
                    <Button variant="outline" size="sm" className="text-xs" data-testid={`vv-join-${v.id}`}
                      onClick={() => setActiveCall(v)}>
                      <Play className="w-3 h-3 mr-1" /> Join Video
                    </Button>
                  )}
                  {v.status !== 'completed' && (
                    <Button variant="outline" size="sm" className="text-xs" onClick={() => setNotesVisit(v)}
                      data-testid={`vv-notes-${v.id}`}>
                      <FileText className="w-3 h-3 mr-1" /> Add Notes
                    </Button>
                  )}
                  <Button variant="outline" size="sm" className="text-xs" data-testid={`vv-link-${v.id}`}
                    onClick={() => navigator.clipboard.writeText(`${window.location.origin}${v.family_link}`)}>
                    <Link2 className="w-3 h-3 mr-1" /> Copy Family Link
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}


// ═══════════════════════════════════════════════════════
// BEREAVEMENT JOURNEY
// ═══════════════════════════════════════════════════════
function BereavementJourney({ patients }) {
  const [journeys, setJourneys] = useState([]);
  const [selectedJourney, setSelectedJourney] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ patient_id: '', family_contact_name: '', family_contact_email: '', family_contact_phone: '', relationship: 'spouse', date_of_passing: '' });
  const [creating, setCreating] = useState(false);
  const [generatingId, setGeneratingId] = useState(null);
  const [generatingAll, setGeneratingAll] = useState(false);
  const [resources, setResources] = useState([]);

  useEffect(() => {
    Promise.all([
      fetch(`${API}/api/wave4/bereavement/journeys`).then(r => r.json()),
      fetch(`${API}/api/wave4/bereavement/resources`).then(r => r.json()),
    ]).then(([j, r]) => {
      setJourneys(j.journeys || []);
      setResources(r.resources || []);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const createJourney = async () => {
    if (!formData.patient_id || !formData.family_contact_name) return;
    setCreating(true);
    try {
      const res = await fetch(`${API}/api/wave4/bereavement/start`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      const journey = await res.json();
      setJourneys(prev => [{ id: journey.id, patient_name: journey.patient_name, family_contact_name: journey.family_contact_name, relationship: journey.relationship, date_of_passing: journey.date_of_passing, status: journey.status, created_at: journey.created_at }, ...prev]);
      setShowForm(false);
      setFormData({ patient_id: '', family_contact_name: '', family_contact_email: '', family_contact_phone: '', relationship: 'spouse', date_of_passing: '' });
    } catch {}
    setCreating(false);
  };

  const loadJourney = async (id) => {
    try {
      const res = await fetch(`${API}/api/wave4/bereavement/journey/${id}`);
      setSelectedJourney(await res.json());
    } catch {}
  };

  const generateMessage = async (journeyId, milestoneId) => {
    setGeneratingId(milestoneId);
    try {
      const res = await fetch(`${API}/api/wave4/bereavement/generate-message/${journeyId}/${milestoneId}`, { method: 'POST' });
      const data = await res.json();
      setSelectedJourney(prev => ({
        ...prev,
        milestones: prev.milestones.map(m => m.id === milestoneId ? { ...m, message: data.message, status: 'generated' } : m)
      }));
    } catch {}
    setGeneratingId(null);
  };

  const generateAll = async (journeyId) => {
    setGeneratingAll(true);
    try {
      await fetch(`${API}/api/wave4/bereavement/generate-all/${journeyId}`, { method: 'POST' });
      await loadJourney(journeyId);
    } catch {}
    setGeneratingAll(false);
  };

  const milestoneTypeColors = {
    immediate: 'border-l-red-400',
    comfort: 'border-l-amber-400',
    check_in: 'border-l-sky-300',
    milestone: 'border-l-sky-300',
    anniversary: 'border-l-terra-400',
  };

  return (
    <div data-testid="bereavement-journey">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Heart className="w-5 h-5 text-terra-400" />
          <h2 className="text-lg font-heading font-bold text-[#2C3D30]">Bereavement Journey</h2>
          <Badge className="bg-terra-50 text-terra-400 text-xs">Compassionate Care</Badge>
        </div>
        <Button onClick={() => setShowForm(!showForm)} size="sm" className="bg-terra-400 hover:bg-terra-500"
          data-testid="bv-new-btn">
          <Plus className="w-3.5 h-3.5 mr-1" /> Start Journey
        </Button>
      </div>

      {showForm && (
        <Card className="mb-4 border-terra-200" data-testid="bv-form">
          <CardContent className="pt-4 space-y-3">
            <select value={formData.patient_id} onChange={e => setFormData(p => ({ ...p, patient_id: e.target.value }))}
              className="w-full rounded-xl border border-sand-200 px-3 py-2 text-sm bg-sand-50" data-testid="bv-patient-select">
              <option value="">Select patient...</option>
              {patients.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
            <Input value={formData.family_contact_name} onChange={e => setFormData(p => ({ ...p, family_contact_name: e.target.value }))}
              placeholder="Family member name" data-testid="bv-family-name" />
            <div className="grid grid-cols-2 gap-2">
              <Input value={formData.family_contact_email} onChange={e => setFormData(p => ({ ...p, family_contact_email: e.target.value }))}
                placeholder="Email" type="email" data-testid="bv-email" />
              <Input value={formData.family_contact_phone} onChange={e => setFormData(p => ({ ...p, family_contact_phone: e.target.value }))}
                placeholder="Phone" type="tel" data-testid="bv-phone" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <select value={formData.relationship} onChange={e => setFormData(p => ({ ...p, relationship: e.target.value }))}
                className="rounded-xl border border-sand-200 px-3 py-2 text-sm bg-sand-50" data-testid="bv-relationship">
                <option value="spouse">Spouse</option>
                <option value="child">Son/Daughter</option>
                <option value="parent">Parent</option>
                <option value="sibling">Sibling</option>
                <option value="friend">Friend</option>
              </select>
              <Input type="date" value={formData.date_of_passing} onChange={e => setFormData(p => ({ ...p, date_of_passing: e.target.value }))}
                data-testid="bv-date" />
            </div>
            <Button onClick={createJourney} disabled={creating || !formData.patient_id || !formData.family_contact_name}
              className="w-full bg-terra-400 hover:bg-terra-500" data-testid="bv-create-btn">
              {creating ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Heart className="w-4 h-4 mr-1" />}
              Begin Bereavement Journey
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Journey Detail */}
      {selectedJourney && (
        <Card className="mb-4 border-terra-200" data-testid="bv-detail">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="text-sm font-semibold text-[#2C3D30]">Journey for {selectedJourney.family_contact_name}</p>
                <p className="text-xs text-[#5C6A5F]">In memory of {selectedJourney.patient_name} &middot; Passed {selectedJourney.date_of_passing}</p>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => generateAll(selectedJourney.id)} disabled={generatingAll}
                  className="text-xs" data-testid="bv-generate-all-btn">
                  {generatingAll ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Sparkles className="w-3 h-3 mr-1" />}
                  Generate All
                </Button>
                <button onClick={() => setSelectedJourney(null)} className="text-sand-400 hover:text-[#5C6A5F]"><X className="w-4 h-4" /></button>
              </div>
            </div>

            <div className="space-y-2">
              {selectedJourney.milestones?.map(m => (
                <div key={m.id} className={`p-3 rounded-xl bg-sand-50 border-l-4 ${milestoneTypeColors[m.type] || 'border-l-slate-300'} border border-sand-200`}>
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-medium text-[#2C3D30]">{m.label}</span>
                      <span className="text-xs text-sand-400">{m.target_date}</span>
                    </div>
                    <Badge className={m.status === 'generated' ? 'bg-sage-50 text-sage-400' : 'bg-sand-200 text-[#5C6A5F]'} variant="outline">
                      {m.status === 'generated' ? 'Ready' : 'Pending'}
                    </Badge>
                  </div>
                  {m.message ? (
                    <p className="text-xs text-[#5C6A5F] leading-relaxed whitespace-pre-wrap mt-1">{m.message}</p>
                  ) : (
                    <Button variant="outline" size="sm" onClick={() => generateMessage(selectedJourney.id, m.id)}
                      disabled={generatingId === m.id} className="mt-1 text-xs">
                      {generatingId === m.id ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Sparkles className="w-3 h-3 mr-1" />}
                      Generate Message
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Journey List */}
      {loading ? (
        <div className="flex items-center justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-terra-400" /></div>
      ) : !selectedJourney && (
        <>
          {journeys.length === 0 ? (
            <div className="text-center py-12 text-sand-400">
              <Heart className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">No bereavement journeys started yet.</p>
            </div>
          ) : (
            <div className="space-y-2" data-testid="bv-list">
              {journeys.map(j => (
                <button key={j.id} onClick={() => loadJourney(j.id)}
                  className="w-full text-left p-3 rounded-xl border border-sand-200 hover:border-terra-300 hover:bg-terra-50 transition-colors"
                  data-testid={`bv-journey-${j.id}`}>
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-sm font-medium text-[#2C3D30]">{j.family_contact_name} ({j.relationship})</p>
                      <p className="text-xs text-[#5C6A5F]">In memory of {j.patient_name}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-sand-400">{j.date_of_passing}</p>
                      <ArrowRight className="w-3.5 h-3.5 text-sand-400 ml-auto mt-1" />
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}

          {/* Resources */}
          {resources.length > 0 && (
            <div className="mt-6" data-testid="bv-resources">
              <p className="text-xs font-medium text-[#5C6A5F] mb-3">Bereavement Resources</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {resources.slice(0, 6).map((r, i) => (
                  <div key={i} className="p-2.5 rounded-xl bg-sand-50 border border-sand-200">
                    <Badge variant="outline" className="text-xs mb-1">{r.category}</Badge>
                    <p className="text-xs font-medium text-[#2C3D30]">{r.title}</p>
                    <p className="text-xs text-sand-400 mt-0.5">{r.description}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}


// ═══════════════════════════════════════════════════════
// MAIN PAGE
// ═══════════════════════════════════════════════════════
export default function Wave4FeaturesPage() {
  const [patients, setPatients] = useState([]);
  const [activeTab, setActiveTab] = useState('nl');

  useEffect(() => {
    fetch(`${API}/api/wave2/patients-list`)
      .then(r => r.ok ? r.json() : { patients: [] })
      .then(d => setPatients(d.patients || []))
      .catch(() => {});
  }, []);

  const tabs = [
    { id: 'nl', label: 'NL Operations', icon: Sparkles, activeClass: 'bg-sky-50 text-sky-500 border border-sky-200' },
    { id: 'video', label: 'Video Visits', icon: Video, activeClass: 'bg-sage-50 text-sage-400 border border-sage-200' },
    { id: 'bereavement', label: 'Bereavement', icon: Heart, activeClass: 'bg-terra-50 text-terra-400 border border-terra-200' },
  ];

  return (
    <div className="min-h-screen bg-sand-100" data-testid="wave4-page">
      <div className="max-w-4xl mx-auto p-6">
        <div className="mb-6">
          <h1 className="text-2xl font-heading font-bold text-[#2C3D30]">Advanced Experience</h1>
          <p className="text-sm text-[#5C6A5F] mt-1">Operations intelligence, virtual care, and compassionate bereavement support</p>
        </div>

        <div className="flex gap-2 mb-6 border-b border-sand-200 pb-3">
          {tabs.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
                activeTab === tab.id ? tab.activeClass : 'text-[#5C6A5F] hover:text-[#2C3D30] hover:bg-sand-100'
              }`} data-testid={`wave4-tab-${tab.id}`}>
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        <Card className="shadow-sm">
          <CardContent className="p-6">
            {activeTab === 'nl' && <NLExpansion />}
            {activeTab === 'video' && <VideoVisits patients={patients} />}
            {activeTab === 'bereavement' && <BereavementJourney patients={patients} />}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
